import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Badge, Menu } from 'antd';
import { openReportsStore } from '../contentReports/openReportsStore';
import { useCurrentUser } from '../../user/permissions';
import { CONTENT_REPORTS_KEY, groupOfPage, pageForPath, visibleNavGroups, visiblePages } from './navItems';

const logoModule = require('../../assets/logo.svg');
const logoUrl = typeof logoModule === 'string' ? logoModule : (logoModule?.default ?? '');

export { visiblePages as visibleNavItems };

const OPEN_REPORTS_POLL_MS = 60 * 1000;

const withBadge = (label, count) => (
    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        {label}
        <Badge count={count} overflowCount={99} size="small" title={`${count} open`} />
    </span>
);

export const NavBar = observer(function NavBar() {
    const location = useLocation();
    const history = useHistory();
    const currentUserStore = useCurrentUser();
    const groups = visibleNavGroups(currentUserStore);
    const seesContentReports = visiblePages(currentUserStore).some((page) => page.key === CONTENT_REPORTS_KEY);

    // Reports arrive from the apps at any time: refresh on every navigation and once a minute.
    useEffect(() => {
        if (seesContentReports) openReportsStore.refresh();
    }, [location.pathname, seesContentReports]);
    useEffect(() => {
        if (!seesContentReports) return undefined;
        const timer = setInterval(() => openReportsStore.refresh(), OPEN_REPORTS_POLL_MS);
        return () => clearInterval(timer);
    }, [seesContentReports]);

    const selectedKey = pageForPath(location.pathname)?.key;
    const currentGroup = selectedKey ? groupOfPage(selectedKey) : null;
    const [openKeys, setOpenKeys] = useState(currentGroup ? [currentGroup] : []);
    useEffect(() => {
        if (currentGroup) setOpenKeys((keys) => (keys.includes(currentGroup) ? keys : [...keys, currentGroup]));
    }, [currentGroup]);

    const openReports = openReportsStore.count;
    const pageItem = (page) => ({
        key: page.key,
        label: page.key === CONTENT_REPORTS_KEY
            ? withBadge(page.label, openReports)
            : page.bold ? <strong>{page.label}</strong> : page.label,
    });
    const items = groups.map((entry) => !entry.children ? { key: entry.key, label: entry.label } : {
        key: entry.key,
        label: entry.children.some((page) => page.key === CONTENT_REPORTS_KEY) && !openKeys.includes(entry.key)
            ? withBadge(entry.label, openReports)
            : entry.label,
        children: entry.children.map(pageItem),
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <img src={logoUrl} alt="Design With Friends" style={{ height: 32 }} />
            </div>
            <Menu
                mode="inline"
                theme="dark"
                selectedKeys={selectedKey ? [selectedKey] : []}
                openKeys={openKeys}
                onOpenChange={setOpenKeys}
                items={items}
                onClick={({ key }) => history.push(key)}
                style={{ flex: 1, borderRight: 0, paddingTop: 8, overflowY: 'auto' }}
            />
        </div>
    );
});
